import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAuthMe, logoutFetch } from "../../../store/authSlice";
import clinicAPI from "../../../services/clinicApi";
import authAPI from "../../../services/authApi";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { formatDateRu } from "../../../utils/appointmentLabels";
import { ageFromDob } from "../../../utils/clientAppUtils";
import { membershipHeadlineChips } from "../../../utils/clientScheduleUtils";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import ClientClubMembership from "./ClientClubMembership";
import ClientClubGroups from "./ClientClubGroups";
import ClientPushSettings from "./ClientPushSettings";
import ClientSocialSettings from "./ClientSocialSettings";
import ClientChildSwitcher from "./ClientChildSwitcher";
import ClientChildPortalAccess from "./ClientChildPortalAccess";
import { useClientRefresh, dispatchClientRefresh } from "../../../hooks/useClientRefresh";
import { usePlanAccess } from "../../../hooks/usePlanAccess";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { resetClientAppChrome } from "../../../utils/clientLogout";
import s from "./clientApp.module.css";
import { Avatar, Button, Field, Input, PhoneInput, Skeleton, useToast } from "../../ui";

const ACCOUNT_BASE = "/app/profile/account";

function accountSection(pathname) {
  if (!pathname.startsWith(ACCOUNT_BASE)) return "menu";
  const rest = pathname.slice(ACCOUNT_BASE.length).replace(/^\//, "");
  return rest || "menu";
}

const MENU = [
  { id: "club", label: "Абонемент", hint: "Тариф и остаток занятий" },
  { id: "groups", label: "Группы", hint: "Группы и расписание" },
  { id: "social", label: "Оформление", hint: "Обложка, био, приватность" },
  { id: "stats", label: "Рейтинг", hint: "Место в группе по явке", link: "/app/stats" },
  { id: "data", label: "Личные данные", hint: "ФИО, телефон, email" },
  { id: "kids", label: "Дети", hint: "Семья и доступ в кабинет", parentOnly: true },
  { id: "pay", label: "Оплаты", hint: "История платежей" },
  { id: "notify", label: "Уведомления", hint: "Push и напоминания" },
  { id: "pwd", label: "Пароль", hint: "Безопасность входа" },
];

function childSubtitle(athlete) {
  const age = ageFromDob(athlete.date_of_birth);
  const groupNames = (athlete.groups || [])
    .map((g) => g.name)
    .filter(Boolean)
    .join(", ");
  return (
    [
      athlete.membership_label,
      age != null ? `${age} лет` : null,
      athlete.relation,
      groupNames,
    ]
      .filter(Boolean)
      .join(" · ") || "Спортсмен"
  );
}

function formatRub(amount) {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: Number(amount) % 1 === 0 ? 0 : 2,
  }).format(Number(amount) || 0);
}

function formatPayDate(iso) {
  if (!iso) return "";
  return formatDateRu(iso, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function ChildRow({ athlete, onSelect, portalAccess }) {
  const content = (
    <>
      <Avatar name={athlete.full_name || "?"} size="sm" />
      <div className={s.profChildInfo}>
        <strong>{athlete.full_name}</strong>
        <span>{childSubtitle(athlete)}</span>
        {athlete.membership_label ? (
          <span className={s.profChildMembership}>{athlete.membership_label}</span>
        ) : null}
      </div>
    </>
  );

  const card = (
    <div className={s.profChildCard}>
      {onSelect ? (
        <button type="button" className={s.profChildBtn} onClick={() => onSelect(athlete)}>
          {content}
        </button>
      ) : (
        <div className={s.profChild}>{content}</div>
      )}
      {portalAccess}
    </div>
  );

  return card;
}

export default function ClientProfileApp() {
  const dispatch = useDispatch();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile(768);
  const desktop = !isMobile;
  const { profile, tenant } = useSelector((state) => state.auth);
  const { isParentMode, athletes, athleteQueryParam, activeAthleteId, activeAthlete, reloadPortalContext, setActiveAthleteId } =
    useClientAthlete();
  const [searchParams, setSearchParams] = useSearchParams();
  const screen = accountSection(location.pathname);
  const plan = usePlanAccess();
  const [form, setForm] = useState({ full_name: "", phone: "" });
  const [email, setEmail] = useState("");
  const [dependents, setDependents] = useState([]);
  const [dash, setDash] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [payments, setPayments] = useState([]);
  const [prefs, setPrefs] = useState({
    reminder_email_enabled: true,
  });
  const [pwd, setPwd] = useState({ current: "", next: "" });
  const [childForm, setChildForm] = useState({
    full_name: "",
    date_of_birth: "",
    relation: "",
    email: "",
    phone: "",
  });
  const [showChildForm, setShowChildForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [childSaving, setChildSaving] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const photoInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [prof, n, deps, pay, dashboard, sched] = await Promise.all([
        clinicAPI.getMyPatientProfile(),
        clinicAPI.getNotificationPrefs(),
        clinicAPI.getDependents().catch(() => ({ dependents: [] })),
        clinicAPI.getMyPayments(athleteQueryParam).catch(() => ({ payments: [] })),
        clinicAPI.getMyClientDashboard(athleteQueryParam).catch(() => null),
        clinicAPI.getMySchedule(athleteQueryParam).catch(() => ({ sessions: [] })),
      ]);
      const p = prof.patient;
      setForm({ full_name: p?.full_name || "", phone: p?.phone || "" });
      setEmail(p?.email || "");
      setPhotoUrl(p?.photo_url || null);
      setDependents(deps.dependents || []);
      setPayments(pay.payments || []);
      setDash(dashboard);
      setSchedule(sched.sessions || []);
      setPrefs({
        reminder_email_enabled: n.reminder_email_enabled !== false,
      });
    } finally {
      setLoading(false);
    }
  }, [athleteQueryParam]);

  useEffect(() => {
    load();
  }, [load]);

  useClientRefresh(load);

  useEffect(() => {
    const pay = searchParams.get("payment");
    if (!pay) return;
    if (pay === "success") {
      toast.success("Оплата прошла успешно");
      load();
      dispatchClientRefresh();
    } else if (pay === "fail") {
      toast.error("Оплата не завершена");
    }
    const next = new URLSearchParams(searchParams);
    next.delete("payment");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, load, toast]);

  useEffect(() => {
    if (screen === "menu") return;
    if (screen === "social" && !plan.memberSocial) {
      navigate(ACCOUNT_BASE, { replace: true });
      return;
    }
    if (!MENU.some((m) => m.id === screen && !m.link)) {
      navigate(ACCOUNT_BASE, { replace: true });
    }
  }, [screen, navigate, plan.memberSocial]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    try {
      const r = await clinicAPI.uploadMyPatientPhoto(file);
      const url = r.photo_url || r.patient?.photo_url || null;
      setPhotoUrl(url ? `${url}${url.includes("?") ? "&" : "?"}v=${Date.now()}` : null);
      toast.success("Фото обновлено");
      dispatch(fetchAuthMe());
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка загрузки");
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await clinicAPI.updateMyPatientProfile({
        full_name: form.full_name,
        phone: form.phone,
        reminder_email_enabled: prefs.reminder_email_enabled,
      });
      await clinicAPI.updateNotificationPrefs(prefs);
      toast.success("Сохранено");
      dispatch(fetchAuthMe());
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const addChild = async (e) => {
    e.preventDefault();
    if (!childForm.full_name.trim()) {
      toast.error("Укажите имя");
      return;
    }
    setChildSaving(true);
    try {
      const r = await clinicAPI.addDependent(childForm);
      if (r.invite_email_sent) {
        toast.success("Ребёнок добавлен, приглашение отправлено на email");
      } else if (r.invite_url) {
        try {
          await navigator.clipboard.writeText(r.invite_url);
          toast.success("Ребёнок добавлен. Ссылка для входа скопирована");
        } catch {
          toast.success("Ребёнок добавлен");
        }
      } else {
        toast.success("Ребёнок добавлен в семейный кабинет");
      }
      setChildForm({ full_name: "", date_of_birth: "", relation: "", email: "", phone: "" });
      setShowChildForm(false);
      await reloadPortalContext();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка");
    } finally {
      setChildSaving(false);
    }
  };

  const changePassword = async () => {
    if (pwd.next.length < 8) {
      toast.error("Пароль от 8 символов");
      return;
    }
    try {
      await authAPI.changePassword(pwd.current, pwd.next);
      toast.success("Пароль изменён");
      setPwd({ current: "", next: "" });
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const logout = () => {
    if (loggingOut) return;
    setLoggingOut(true);
    resetClientAppChrome();
    dispatch(logoutFetch());
    navigate("/login", { replace: true });
  };

  const mergedFamilyChildren = useMemo(() => {
    const depById = new Map(
      dependents
        .filter((d) => d.athlete_id || d.id)
        .map((d) => [String(d.athlete_id || d.id), d]),
    );
    const fromAthletes = isParentMode ? athletes.filter((a) => !a.is_self) : [];
    if (fromAthletes.length) {
      return fromAthletes.map((a) => ({
        ...depById.get(String(a.id)),
        ...a,
      }));
    }
    return dependents.filter((d) => d.athlete_id);
  }, [athletes, dependents, isParentMode]);

  const updateChildPortal = useCallback((athleteId, patch) => {
    setDependents((prev) =>
      prev.map((d) =>
        String(d.athlete_id || d.id) === String(athleteId) ? { ...d, ...patch } : d,
      ),
    );
    reloadPortalContext();
  }, [reloadPortalContext]);

  if (loading) {
    return <Skeleton height={300} style={{ borderRadius: 16 }} />;
  }

  const membership = dash?.membership;
  const headChips = membershipHeadlineChips(membership, dash?.stats);
  const displayEmail =
    email && !email.includes("@patient.internal") ? email : null;
  const activeMenu = MENU.find((m) => m.id === screen);

  const linkedAthleteIds = new Set(athletes.map((a) => String(a.id)));
  const extraDependents = dependents.filter(
    (d) => !d.athlete_id || !linkedAthleteIds.has(String(d.athlete_id)),
  );
  const showKidsMenu =
    isParentMode || extraDependents.length > 0;
  const visibleMenu = MENU.filter(
    (item) =>
      (!item.parentOnly || showKidsMenu) &&
      (item.id !== "social" || plan.memberSocial),
  );

  const viewingChild =
    isParentMode && activeAthlete && !activeAthlete.is_self;
  const showFamilySwitcher = isParentMode && athletes.length > 1;

  const renderPanel = () => {
    if (screen === "club") {
      return (
        <>
          {viewingChild ? (
            <p className={s.profFamilyContext}>
              Абонемент: <strong>{activeAthlete.full_name}</strong>
            </p>
          ) : null}
          <ClientClubMembership
            dash={dash}
            patientId={activeAthleteId || profile?.id}
            onChanged={() => {
              clinicAPI
                .getMyClientDashboard(athleteQueryParam)
                .then((d) => setDash(d))
                .catch(() => {});
            }}
          />
        </>
      );
    }
    if (screen === "groups") {
      return (
        <>
          {viewingChild ? (
            <p className={s.profFamilyContext}>
              Группы: <strong>{activeAthlete.full_name}</strong>
            </p>
          ) : null}
          <ClientClubGroups dash={dash} schedule={schedule} />
        </>
      );
    }
    if (screen === "social") {
      return <ClientSocialSettings />;
    }
    if (screen === "data") {
      return (
        <>
          <button
            type="button"
            className={s.profDataPhotoBtn}
            onClick={() => photoInputRef.current?.click()}
            disabled={photoUploading}
          >
            <Avatar
              name={profile?.full_name || form.full_name || "?"}
              src={mediaUrl(photoUrl || profile?.photo_url)}
              size="lg"
            />
            <span className={s.profDataPhotoHint}>
              {photoUploading ? "Загрузка…" : "Нажмите, чтобы сменить фото"}
            </span>
          </button>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={onPhoto}
          />
          <Field label="ФИО">
            <Input
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            />
          </Field>
          <Field label="Телефон">
            <PhoneInput
              value={form.phone || ""}
              onChange={(v) => setForm({ ...form, phone: v })}
            />
          </Field>
          {displayEmail ? (
            <Field label="Email">
              <Input value={displayEmail} disabled />
            </Field>
          ) : null}
          <Button type="button" onClick={save} disabled={saving}>
            {saving ? "…" : "Сохранить"}
          </Button>
        </>
      );
    }
    if (screen === "kids") {
      const openChild = (athlete) => {
        setActiveAthleteId(athlete.id);
        navigate(`${ACCOUNT_BASE}/club`);
      };

      return (
        <>
          <p className={s.profKidsLead}>
            У каждого ребёнка может быть свой вход в кабинет — укажите email и
            отправьте приглашение. Родитель по-прежнему видит всю семью.
          </p>

          {mergedFamilyChildren.length === 0 && extraDependents.length === 0 ? (
            <p className={s.profMuted}>
              Здесь отображаются профили детей, привязанные к вашему аккаунту.
              Обратитесь к администратору клуба, если нужно добавить ребёнка.
            </p>
          ) : null}

          {mergedFamilyChildren.map((athlete) => (
            <ChildRow
              key={athlete.id}
              athlete={athlete}
              onSelect={openChild}
              portalAccess={
                <ClientChildPortalAccess
                  athlete={athlete}
                  onUpdated={(patch) => updateChildPortal(athlete.id, patch)}
                />
              }
            />
          ))}

          {extraDependents.map((d) => (
            <ChildRow
              key={d.id}
              athlete={{
                full_name: d.full_name,
                date_of_birth: d.date_of_birth,
                relation: d.relation,
                groups: [],
              }}
            />
          ))}

          {showKidsMenu ? (
            showChildForm ? (
              <form className={s.profChildForm} onSubmit={addChild}>
                <Field label="ФИО">
                  <Input
                    value={childForm.full_name}
                    onChange={(e) =>
                      setChildForm({ ...childForm, full_name: e.target.value })
                    }
                  />
                </Field>
                <Field label="Дата рождения">
                  <Input
                    type="date"
                    value={childForm.date_of_birth}
                    onChange={(e) =>
                      setChildForm({ ...childForm, date_of_birth: e.target.value })
                    }
                  />
                </Field>
                <Field label="Кем приходится">
                  <Input
                    value={childForm.relation}
                    placeholder="Сын, дочь…"
                    onChange={(e) =>
                      setChildForm({ ...childForm, relation: e.target.value })
                    }
                  />
                </Field>
                <Field
                  label="Email ребёнка"
                  hint="Если указать — отправим приглашение для входа в личный кабинет"
                >
                  <Input
                    type="email"
                    value={childForm.email}
                    onChange={(e) =>
                      setChildForm({ ...childForm, email: e.target.value })
                    }
                    placeholder="Необязательно"
                  />
                </Field>
                <Field label="Телефон ребёнка">
                  <PhoneInput
                    value={childForm.phone || ""}
                    onChange={(v) =>
                      setChildForm({ ...childForm, phone: v })
                    }
                  />
                </Field>
                <div className={s.profChildFormBtns}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowChildForm(false)}
                  >
                    Отмена
                  </Button>
                  <Button type="submit" disabled={childSaving}>
                    {childSaving ? "…" : "Добавить"}
                  </Button>
                </div>
              </form>
            ) : (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setShowChildForm(true)}
              >
                + Добавить ребёнка
              </Button>
            )
          ) : null}
        </>
      );
    }
    if (screen === "pay") {
      return (
        <>
          {viewingChild ? (
            <p className={s.profFamilyContext}>
              Оплаты: <strong>{activeAthlete.full_name}</strong>
            </p>
          ) : null}
          {payments.length === 0 ? (
            <div className={s.profPayEmpty}>
              <span className={s.profPayEmptyIcon} aria-hidden>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="5" width="20" height="14" rx="3" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M2 10h20" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </span>
              <p>Оплат пока нет</p>
              <small>Здесь появятся платежи, которые отметил тренер или администратор</small>
            </div>
          ) : (
            <ul className={s.profPayList}>
              {payments.map((p) => (
                <li key={p.id} className={s.profPayCard}>
                  <div className={s.profPayCardTop}>
                    <div className={s.profPayCardMain}>
                      <strong>{p.description || "Оплата"}</strong>
                      <span className={s.profPayDate}>{formatPayDate(p.date)}</span>
                    </div>
                    <span className={s.profPayAmount}>{formatRub(p.amount)}</span>
                  </div>
                  <div className={s.profPayCardMeta}>
                    {p.method_label ? (
                      <span className={s.profPayBadge}>{p.method_label}</span>
                    ) : null}
                    {p.status === "paid" ? (
                      <span className={`${s.profPayBadge} ${s.profPayBadgeOk}`}>
                        Оплачено
                      </span>
                    ) : null}
                  </div>
                  {p.notes ? <p className={s.profPayNote}>{p.notes}</p> : null}
                </li>
              ))}
            </ul>
          )}
        </>
      );
    }
    if (screen === "notify") {
      return (
        <>
          <label className={s.profCheck}>
            <input
              type="checkbox"
              checked={prefs.reminder_email_enabled}
              onChange={(e) =>
                setPrefs({ ...prefs, reminder_email_enabled: e.target.checked })
              }
            />
            Email о расписании за день до занятия
          </label>
          <p className={s.profPayNote}>
            Push о тренировках — включите уведомления в приложении ниже (нужно
            добавить клуб на главный экран). Остальные события (чат, опросы) —
            через push или email в профиле.
          </p>
          <ClientPushSettings />
          <Button type="button" size="sm" onClick={save} disabled={saving}>
            {saving ? "…" : "Сохранить"}
          </Button>
        </>
      );
    }
    if (screen === "pwd") {
      return (
        <>
          <Field label="Текущий пароль">
            <Input
              type="password"
              value={pwd.current}
              onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
            />
          </Field>
          <Field label="Новый пароль">
            <Input
              type="password"
              value={pwd.next}
              onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
            />
          </Field>
          <Button type="button" variant="secondary" onClick={changePassword}>
            Сменить пароль
          </Button>
        </>
      );
    }
    return null;
  };

  if (screen !== "menu") {
    return (
      <div className={s.profPage}>
        <header className={s.profSubHead}>
          <button
            type="button"
            className={s.profBackBtn}
            onClick={() => navigate(ACCOUNT_BASE)}
          >
            ← {desktop ? "Настройки" : "Назад"}
          </button>
          <h1>{activeMenu?.label}</h1>
          {activeMenu?.hint ? <p>{activeMenu.hint}</p> : null}
        </header>
        {showFamilySwitcher ? (
          <div className={s.profFamilySwitcher}>
            <ClientChildSwitcher compact />
          </div>
        ) : null}
        <div
          className={
            screen === "social" ? s.profSocialWrap : s.profSubBody
          }
        >
          {renderPanel()}
        </div>
      </div>
    );
  }

  return (
    <div className={s.profPage}>
      {isMobile ? (
        <header className={s.profSubHead}>
          <button
            type="button"
            className={s.profBackBtn}
            onClick={() => navigate("/app/profile")}
          >
            ← Профиль
          </button>
          <h1>Настройки</h1>
          <p>Абонемент, группы и аккаунт</p>
        </header>
      ) : null}

      <div className={s.profAccountUser}>
        <button
          type="button"
          className={s.profAccountUserMain}
          onClick={() => navigate(`${ACCOUNT_BASE}/data`)}
        >
          <div className={s.profAccountUserAvatar}>
            <Avatar
              name={profile?.full_name || form.full_name || "?"}
              src={mediaUrl(photoUrl || profile?.photo_url)}
              size="md"
            />
          </div>
          <div className={s.profAccountUserText}>
            <strong>{form.full_name || profile?.full_name}</strong>
            <span>{tenant?.name || "Клуб"}</span>
            <span className={s.profAccountUserEdit}>Личные данные и фото</span>
          </div>
          <span className={s.profAccountUserChevron} aria-hidden>
            ›
          </span>
        </button>
        {headChips.length > 0 ? (
          <div className={s.profAccountUserChips}>
            {headChips.map((chip) => (
              <span
                key={chip.key}
                className={chip.accent ? `${s.profChip} ${s.profChipAccent}` : s.profChip}
              >
                {chip.label}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {showFamilySwitcher ? (
        <div className={s.profFamilySwitcher}>
          <ClientChildSwitcher />
        </div>
      ) : null}

      <nav className={s.profNavList} aria-label="Настройки профиля">
        {visibleMenu.map((item) =>
          item.link ? (
            <Link key={item.id} to={item.link} className={s.profNavRow}>
              <span className={s.profNavText}>
                <strong>{item.label}</strong>
                <small>{item.hint}</small>
              </span>
              <span className={s.profNavChevron} aria-hidden>
                ›
              </span>
            </Link>
          ) : (
            <button
              key={item.id}
              type="button"
              className={s.profNavRow}
              onClick={() => navigate(`${ACCOUNT_BASE}/${item.id}`)}
            >
              <span className={s.profNavText}>
                <strong>{item.label}</strong>
                <small>{item.hint}</small>
              </span>
              <span className={s.profNavChevron} aria-hidden>
                ›
              </span>
            </button>
          ),
        )}
      </nav>

      {isMobile ? (
        <button
          type="button"
          className={s.profLogout}
          onClick={logout}
          disabled={loggingOut}
        >
          {loggingOut ? "Выход…" : "Выйти из аккаунта"}
        </button>
      ) : null}
    </div>
  );
}
